export interface BagItem {
  id: number
  slug: string
  title: string
  imageFileId: string | null
  quantity: number
  // How many the revue has of this item in total
  stock?: number
}

export interface BagPeriod {
  fromDate: string
  toDate: string
}

const STORAGE_KEY = 'shopping-bag'

// Today as yyyy-MM-dd in the visitor's own time zone
export const todayIso = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

// The reservation in progress. It lives in localStorage, so it survives a reload or a later visit.
// The server always renders an empty bag; plugins/shopping-bag.client.ts restores it after hydration,
// and `loaded` tells pages when that has happened.
export const useShoppingBag = () => {
  const items = useState<BagItem[]>('shopping-bag', () => [])
  const period = useState<BagPeriod>('shopping-bag-period', () => ({ fromDate: '', toDate: '' }))
  const loaded = useState('shopping-bag-loaded', () => false)

  const persist = () => {
    if (!import.meta.client) { return }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: items.value, period: period.value }))
    } catch {}
  }

  const restore = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
      // Older versions stored only the list of items
      const storedItems = Array.isArray(stored) ? stored : stored?.items
      items.value = Array.isArray(storedItems) ? storedItems : []
      const storedPeriod = !Array.isArray(stored) ? stored?.period : undefined
      // A period that has already started is no longer useful
      period.value = storedPeriod?.fromDate >= todayIso()
        ? { fromDate: storedPeriod.fromDate, toDate: storedPeriod.toDate ?? '' }
        : { fromDate: '', toDate: '' }
    } catch {}
    loaded.value = true
  }

  const addItem = (item: Omit<BagItem, 'quantity'>, quantity = 1) => {
    const existing = items.value.find(i => i.id === item.id)
    if (existing) {
      existing.quantity = Math.min(existing.quantity + quantity, item.stock ?? Infinity)
      existing.stock = item.stock
    } else {
      items.value.push({ ...item, quantity })
    }
    persist()
  }

  const removeItem = (id: number) => {
    items.value = items.value.filter(i => i.id !== id)
    persist()
  }

  const updateQuantity = (id: number, qty: number) => {
    const item = items.value.find(i => i.id === id)
    if (item) {
      item.quantity = Math.max(1, qty)
      persist()
    }
  }

  const setPeriod = (value: BagPeriod) => {
    period.value = { ...value }
    persist()
  }

  const clear = () => {
    items.value = []
    period.value = { fromDate: '', toDate: '' }
    persist()
  }

  const quantityOf = (id: number) => items.value.find(i => i.id === id)?.quantity ?? 0
  const itemCount = computed(() => items.value.reduce((acc, i) => acc + i.quantity, 0))

  return { items, period, loaded, restore, addItem, removeItem, updateQuantity, setPeriod, clear, quantityOf, itemCount }
}
